import React, {useCallback, useEffect, useState} from 'react'

import ArrowDownSLineIcon from 'remixicon-react/ArrowDownSLineIcon'
import styled from 'styled-components'
import {palette} from 'styled-tools'
import {useDebouncedCallback} from 'use-debounce'

import {
  ChoiceFacet,
  FacetTreeItem,
  HierarchicalFacet,
  HierarchicalFacetOption,
  RangeFacetType,
  sortFacetOptions,
} from '@festi/common/api/algolia'
import {Expandable} from '@festi/common/components/common'
import {PriceRangeSlider} from '@festi/common/components/form'
import {useEvents} from '@festi/common/contexts'
import {useSearch} from '@festi/common/contexts/search'
import {styledTheme} from '@festi/common/themes'

import FacetItem from './Facet'
import FacetCheckboxItem from './FacetCheckboxItem'

interface Props {
  label: string
  activeCount?: number
  children?: React.ReactNode
  initiallyOpen?: boolean
}

const Wrapper = styled.div`
  border-bottom: 1px solid ${palette('border')};
  margin-bottom: 16px;
  transition: border-color 0.15s;
`

const GroupBtn = styled.button`
  cursor: pointer;
  width: 100%;
  border: 0;
  border-top: 1px solid ${palette('border')};
  border-radius: 0;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: transparent;
  transition: border-color 0.15s, background-color 0.15s;
  &:hover {
    background-color: ${palette('lightBlue10')};
  }
`

const BtnTail = styled.div`
  display: flex;
  align-items: center;
  padding-left: 16px;
`

const IconWrapper = styled.div`
  transform: rotate(-90deg);
  transition: transform 0.2s ease-in-out;
`

const Label = styled.span`
  font-size: 1.125rem;
  font-weight: 500;
  color: ${palette('blue')};
  line-height: 1.1;
  text-align: left;
`

const Count = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  width: 24px;
  background-color: ${palette('ui10Solid')};
  border-radius: 50%;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${palette('blue')};
  line-height: 1;
  margin-right: 16px;
  transition: opacity 0.15s;
`

const Content = styled.div`
  padding: 8px 16px 24px 16px;
`

export interface FacetProps {
  attr: string
  label: string
}

export interface ChoiceFacetProps extends FacetProps {
  facet: ChoiceFacet
}

export interface HierarchicalFacetProps extends FacetProps {
  facet: HierarchicalFacet
}

export interface RangeFacetProps extends FacetProps {
  facet: RangeFacetType
}

function Facet({
  label,
  activeCount,
  children,
  initiallyOpen = false,
}: Props): JSX.Element {
  const [isOpen, setIsOpen] = useState<boolean>(initiallyOpen)

  return (
    <Wrapper style={{borderColor: isOpen && styledTheme.palette.lightBlue}}>
      <GroupBtn
        onClick={() => setIsOpen(!isOpen)}
        style={{
          borderColor: isOpen && styledTheme.palette.lightBlue,
          backgroundColor: isOpen && styledTheme.palette.lightBlue10,
        }}
      >
        <Label>{label}</Label>

        <BtnTail>
          <Count style={{opacity: activeCount ? '1' : '0'}}>
            {activeCount}
          </Count>

          <IconWrapper style={{transform: isOpen && 'rotate(0)'}}>
            <ArrowDownSLineIcon
              size={20}
              color={
                isOpen
                  ? styledTheme.palette.lightBlue
                  : styledTheme.palette.ui20Solid
              }
            />
          </IconWrapper>
        </BtnTail>
      </GroupBtn>

      <Expandable expanded={isOpen}>
        <Content>{children}</Content>
      </Expandable>
    </Wrapper>
  )
}

export function CheckboxFacet({label, attr, facet}: ChoiceFacetProps) {
  const {updateFilters, facetOrderValues} = useSearch()
  const sortedOptions = sortFacetOptions(attr, facet.options, facetOrderValues)
  const {sendFilterClickedEvent} = useEvents()

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateFilters({[attr]: {[e.target.value]: e.target.checked}})

      if (e.target.checked) {
        sendFilterClickedEvent(
          'clickedFilters',
          'Filter clicked',
          `${attr}:${e.target.value}`,
        )
      }
    },
    [updateFilters, attr, sendFilterClickedEvent],
  )

  return (
    <Facet label={label}>
      {sortedOptions.map((o) => (
        <FacetCheckboxItem
          key={o.value}
          count={o.count}
          onChange={onChange}
          checked={o.active}
          value={o.value}
        >
          {o.label}
        </FacetCheckboxItem>
      ))}
    </Facet>
  )
}

export function RangeFacet({label, attr, facet}: RangeFacetProps) {
  const {updateFilters} = useSearch()

  const [minValue, setMinValue] = useState<number>(facet.minValue)
  const [maxValue, setMaxValue] = useState<number>(facet.maxValue)

  const update = useDebouncedCallback(() => {
    updateFilters({
      [attr]: {
        [facet.minAttr]: minValue || false,
        [facet.maxAttr]: maxValue,
      },
    })
  }, 200)

  const handleChange = useCallback(
    ([from, to]: number[]) => {
      setMinValue(from)
      setMaxValue(to)
      update()
    },
    [update],
  )

  useEffect(() => {
    if (facet.minValue == null) {
      setMinValue(facet.min)
    }
    if (facet.maxValue == null) {
      setMaxValue(facet.max)
    }
  }, [facet.min, facet.minValue, facet.max, facet.maxValue])

  if (facet.min === facet.max) {
    return null
  }

  return (
    <Facet label={label}>
      <PriceRangeSlider
        min={facet.min}
        max={facet.max}
        minValue={minValue < facet.min ? facet.min : minValue}
        maxValue={maxValue > facet.max ? facet.max : maxValue}
        data={facet.data}
        onChange={handleChange}
      />
    </Facet>
  )
}

function convertToFacetTree(
  facets: HierarchicalFacetOption[],
): Map<string | number, FacetTreeItem> {
  const facetPairs = facets.map<[string | number, FacetTreeItem]>((f) => [
    f.value,
    {...f, children: [], parents: []},
  ])

  const tree = new Map(facetPairs)

  tree.forEach((ref: FacetTreeItem) => {
    tree.get(ref.parent)?.children.push(ref)
  })

  tree.forEach((ref: FacetTreeItem) => {
    ref.children?.forEach((child) => {
      child.parents.push(tree.get(child.parent))
    })
  })

  return tree
}

export function flattenFacetTreeValues(
  facetTreeValues: FacetTreeItem[],
): FacetTreeItem[] {
  return facetTreeValues
    ? facetTreeValues.reduce(
        (result: FacetTreeItem[], item: FacetTreeItem) => [
          ...result,
          item,
          ...flattenFacetTreeValues(item.children),
        ],
        [],
      )
    : []
}

export function HierarchicalCheckboxFacet({
  label,
  attr,
  facet,
}: HierarchicalFacetProps) {
  const {updateFilters, facetOrderValues} = useSearch()
  const sortedOptions = sortFacetOptions(attr, facet.options, facetOrderValues)
  const {sendFilterClickedEvent} = useEvents()
  const facetTree = convertToFacetTree(
    sortedOptions as HierarchicalFacetOption[],
  )
  const facetTreeValues = Array.from(facetTree.values()).filter(
    (p) => !p.parent,
  )
  // const facetTreeValuesFlat: FacetTreeItem[] =
  //   flattenFacetTreeValues(facetTreeValues)

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      const checked = e.target.checked

      const filterUpdate = {[value]: checked}
      // const selectedFacet = facetTreeValuesFlat.find(
      //   (facet: FacetTreeItem) => facet.value.toString() === value,
      // )

      // if un-checking
      /* if (!checked) {
        // check if has children and uncheck them
        if (selectedFacet.children.length > 0) {
          const selectedFacetChildrenFlat = flattenFacetTreeValues(
            selectedFacet.children,
          )
          selectedFacetChildrenFlat.forEach((facet: FacetTreeItem) => {
            filterUpdate[facet.value] = checked
          })
        }

        // check if all siblings are also un-checked
        const parent = facetTree.get(selectedFacet.parent)
        const noActiveChildren = !parent?.children.some(
          (child) => child !== selectedFacet && child.active,
        )
        if (parent && noActiveChildren) {
          // if all parent children are un-checked, add parent facet
          filterUpdate[parent.value] = true
        }
      } */

      // if checking, check if has parent and remove parent facet
      /*       if (checked && selectedFacet.parents) {
        selectedFacet.parents.forEach((parent) => {
          filterUpdate[parent.value] = false
        })
      } */

      updateFilters({[attr]: filterUpdate})

      if (checked) {
        sendFilterClickedEvent(
          'clickedFilters',
          'Filter clicked',
          `${attr}:${value}`,
        )
      }
    },
    [updateFilters, attr, sendFilterClickedEvent],
  )

  return (
    <Facet label={label}>
      {facetTreeValues.map((o: FacetTreeItem) => {
        return (
          <FacetItem key={o.value} option={o} onChange={onChange} level={0} />
        )
      })}
    </Facet>
  )
}

export const facetComponentTypeMap = {
  hierarchical: HierarchicalCheckboxFacet,
  choice: CheckboxFacet,
  range: RangeFacet,
}
